import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material'

import { useUserManagement } from '../../hooks/useUsers'
import UsersTable from '../../components/Users/UsersTable'
import UserForm from '../../components/Users/UserForm'
import RoleGuard from '../../components/Auth/RoleGuard'

const UsersPage = () => {
  const [openForm, setOpenForm] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const {
    users,
    pagination,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    createError,
    updateError,
    deleteError,
    handleSearch,
    handlePageChange,
    handleLimitChange,
    createUser,
    updateUser,
    deleteUser,
    params
  } = useUserManagement()

  const handleOpenCreate = () => {
    setSelectedUser(null)
    setOpenForm(true)
  }

  const handleOpenEdit = (user) => {
    setSelectedUser(user)
    setOpenForm(true)
  }

  const handleCloseForm = () => {
    setOpenForm(false)
    setSelectedUser(null)
  }

  const handleSubmit = async (userData) => {
    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, userData)
      } else {
        await createUser(userData)
      }
      handleCloseForm()
    } catch (error) {
      console.error('Error saving user:', error)
    }
  }

  const handleDeleteClick = (user) => {
    setDeleteConfirm(user)
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirm) {
      try {
        await deleteUser(deleteConfirm.id)
        setDeleteConfirm(null)
      } catch (error) {
        console.error('Error deleting user:', error)
      }
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirm(null)
  }

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchTerm(value)
    handleSearch(value)
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Gestión de Usuarios
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error cargando usuarios: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <RoleGuard requireAdmin redirectTo404>
      <Box>
        {/* Page Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Gestión de Usuarios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Administrar usuarios del sistema
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size="large"
          >
            Nuevo Usuario
          </Button>
        </Box>

        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Buscar usuarios por nombre o email..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
                size="small"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Error Messages */}
        {(createError || updateError || deleteError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createError?.message || updateError?.message || deleteError?.message}
          </Alert>
        )}

        {/* Users Table */}
        <UsersTable
          users={users}
          pagination={pagination}
          loading={isLoading}
          onEdit={handleOpenEdit}
          onDelete={handleDeleteClick}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentPage={params.page}
          pageSize={params.limit}
        />

        {/* User Form Dialog */}
        <Dialog 
          open={openForm} 
          onClose={handleCloseForm}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent>
            <UserForm
              user={selectedUser}
              onSubmit={handleSubmit}
              onCancel={handleCloseForm}
              loading={isCreating || isUpdating}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onClose={handleDeleteCancel}>
          <DialogTitle>Confirmar Desactivación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas desactivar al usuario <strong>{deleteConfirm?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              El usuario no podrá acceder al sistema, pero sus datos se mantendrán.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Desactivando...' : 'Desactivar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RoleGuard>
  )
}

export default UsersPage
